import { useState } from "react";
import { useApolloClient } from "@apollo/client";
import { useParams } from "react-router-dom";
import { IParams } from "@models/IParams";
import {
  PageInfo,
  GroupsManagerSearchFilterFieldsInput,
  AcademicUnit,
  Campus,
  Subject,
  IntFilterInput,
} from "@models/ISchema";
import { FiltersReducerType } from "../context/types";
import { GroupsTableFiltersState } from "../context/useGroupsTableFilters";
import { groupsSearch } from "../graphql/groups.query";
import { groupAdapter } from "../adapters/group";
import { AdaptedGroup } from "../models";

interface IPagination {
  page: number;
  size: number;
}

type UseGetGroupsValue = [
  { response: AdaptedGroup[]; pageInfo: PageInfo; isLoading: boolean },
  (
    filters: FiltersReducerType,
    pagination: IPagination,
    orderBy: GroupsTableFiltersState["orderBy"],
    searchBy: GroupsTableFiltersState["searchBy"],
  ) => void,
];

export const useGetGroups = (): UseGetGroupsValue => {
  const { scenario, origin }: IParams = useParams();
  const client = useApolloClient();
  const [response, setResponse] = useState<AdaptedGroup[]>(null);
  const [pageInfo, setPageInfo] = useState<PageInfo>(null);
  const [isLoading, setIsLoading] = useState(false);

  const addCrnFilter = (
    filters: FiltersReducerType,
    fields: GroupsManagerSearchFilterFieldsInput,
  ): GroupsManagerSearchFilterFieldsInput => ({
    ...fields,
    clientCode: fields.clientCode
      ? {
          ...(fields.clientCode ?? {}),
        }
      : undefined,
    linkId: {
      eq: filters?.selectedCrn?.link?.id as unknown as number,
    },
  });

  const addMainFilters = (
    filters: FiltersReducerType,
    fields: GroupsManagerSearchFilterFieldsInput,
  ): GroupsManagerSearchFilterFieldsInput => {
    const eq = (field: Campus | AcademicUnit | Subject): any => {
      // "*" means `all options`, so nothing should be filtered
      if (!field || field?.id === "*") return undefined;
      return { eq: field?.id };
    };

    // Get course type filters if they exist
    const courseTypesById = filters?.courseTypesById ?? {};
    const courseTypesToFilterBy = Object.values(courseTypesById)
      .map(eq)
      .filter(item => item !== undefined);

    const result: GroupsManagerSearchFilterFieldsInput = {
      ...fields,
      campusId: eq(filters?.campus),
      departmentId: eq(filters?.department),
      schoolId: eq(filters?.school),
      subjectId: eq(filters?.subject),
    };

    if (courseTypesToFilterBy.length > 0) {
      (result as any).courseTypeIds = courseTypesToFilterBy as unknown as IntFilterInput[];
    }

    return result;
  };

  const addAdvancedFilters = (
    filters: FiltersReducerType,
    fields: GroupsManagerSearchFilterFieldsInput,
  ): GroupsManagerSearchFilterFieldsInput => {
    const result = { ...fields };

    if (filters?.state?.active !== filters?.state?.inactive) {
      result.isActive = { is: Boolean(filters?.state?.active) };
    }
    if (filters?.visibility?.visible !== filters?.visibility?.invisible) {
      result.visibleForEnrollment = { is: Boolean(filters?.visibility?.visible) };
    }
    if (filters?.availability?.available !== filters?.availability?.unavailable) {
      result.hasVacancies = { is: Boolean(filters?.availability?.available) };
    }
    if (filters?.enrollments?.with !== filters?.enrollments?.without) {
      result.hasEnrollments = { is: Boolean(filters?.enrollments?.with) };
    }
    if (filters?.owned) {
      result.isOwner = { is: true };
    }

    return result;
  };

  const variables = (
    filters: FiltersReducerType,
    pagination: IPagination,
    orderBy: GroupsTableFiltersState["orderBy"],
    searchBy: GroupsTableFiltersState["searchBy"],
  ) => {
    const filtersToAdd = (fields: any) => {
      if (Boolean(filters?.selectedCrn)) {
        return addCrnFilter(filters, fields);
      } else {
        const main = addMainFilters(filters, fields);
        const advanced = addAdvancedFilters(filters, main);
        return advanced;
      }
    };
    const fields = filtersToAdd({});

    const variables = {
      scenarioId: scenario,
      originId: origin,
      filter: {
        fields: {
          ...fields,
          isGrouped: {
            is: true,
          },
          sumCapacities: {
            is: !filters?.selectedPackage,
          },
          packageId: filters?.selectedPackage
            ? {
                eq: filters?.selectedPackage,
              }
            : undefined,
        },
        pagination,
        orderBy:
          !orderBy || Object.keys(orderBy).length === 0 || orderBy?.field == null
            ? undefined
            : (() => {
                const orderByWithoutHeader = { ...orderBy };
                delete orderByWithoutHeader.header;
                return orderByWithoutHeader;
              })(),
        searchBy:
          !searchBy || Object.keys(searchBy).length === 0 || searchBy?.text == null
            ? undefined
            : searchBy,
      },
    };

    return variables;
  };

  const getGroups = async (
    filters: FiltersReducerType,
    pagination: IPagination,
    orderBy: GroupsTableFiltersState["orderBy"],
    searchBy: GroupsTableFiltersState["searchBy"],
  ) => {
    try {
      setIsLoading(true);

      const { data, errors } = await client.query({
        query: groupsSearch,
        variables: variables(filters, pagination, orderBy, searchBy),
        fetchPolicy: "no-cache",
      });

      if (errors) {
        console.error("[GroupsSearch error]", errors);
        return null;
      }

      setResponse((data?.cube?.groupsManagerSearch?.items ?? []).map(groupAdapter));
      setPageInfo(data?.cube?.groupsManagerSearch?.pageInfo);
    } catch (error) {
      console.error("[GroupsSearch error]: ", error);
    } finally {
      setIsLoading(false);
    }
  };

  return [{ response, pageInfo, isLoading }, getGroups];
};
